import React from 'react';
import { useJobs } from 'src/hooks/useJobs';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { IconFileText, IconPlus } from '@tabler/icons-react';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import { WorkflowStatusPanel } from 'src/design/molecules/WorkflowStatusPanel';
import { WorkflowCreateButton } from 'src/app/workflows/WorkflowCreateButton';
import { WorkflowLogModal } from 'src/app/workflows/WorkflowLogModal';
import { useWorkflowStatusFilters } from 'src/app/workflows/useWorkflowStatusFilters';
import { useWorkflowStatusSegments } from 'src/app/workflows/useWorkflowStatusSegments';
import { useWorkflowLogPdf } from 'src/app/workflows/useWorkflowLogPdf';
import { useTransmittalsList } from 'src/api/queries/transmittals/useTransmittalQueries';
import { useTransmittalLogPdf } from 'src/api/queries/transmittals/useTransmittalMutations';
import { IContact } from 'src/api/models/Organization';
import { TransmittalDto } from 'src/api/FiretrolApiClient';
import { resolveWorkflowButtonColor } from 'src/utils';
import { TransmittalsTable } from './components/TransmittalsTable';

export const Transmittals: React.FC = () => {
  const { selectedJob } = useJobs();
  const {
    data: transmittalList,
    isLoading: loading,
    refetch,
  } = useTransmittalsList(selectedJob?.id);
  const transmittals = React.useMemo<TransmittalDto[]>(
    () => transmittalList ?? [],
    [transmittalList],
  );
  const navigate = useNavigate();
  const location = useLocation();

  const {
    filters,
    covered,
    selectableStatuses,
    statusFilter,
    setStatusFilter,
    filteredItems,
  } = useWorkflowStatusFilters(transmittals, selectedJob?.transmittalWorkflow);
  const segments = useWorkflowStatusSegments(
    transmittals,
    filters,
    covered,
    selectedJob?.transmittalWorkflow,
  );

  const [logOpen, setLogOpen] = React.useState(false);
  const [logStatuses, setLogStatuses] = React.useState<string[]>([]);
  const [logContact, setLogContact] = React.useState<IContact | null>(null);
  const logPdf = useTransmittalLogPdf();
  const { file: logFile, loading: logLoading } = useWorkflowLogPdf({
    open: logOpen,
    jobId: selectedJob?.id,
    statuses: logStatuses,
    contact: logContact,
    fetchPdf: (jobId, statuses, contactId) =>
      logPdf.mutateAsync({ jobId, statuses, contactId }),
    logLabel: 'Failed to load transmittal log PDF',
    onError: () => setLogOpen(false),
  });
  const logUrl = usePdfBlobUrl(logFile?.data);

  const handleOpenLog = () => {
    if (!selectedJob) return;
    setLogOpen(true);
  };

  const closeLog = () => setLogOpen(false);

  if (!selectedJob) return null;

  return (
    <div>
      <Header
        title="Transmittals"
        breadcrumbProps={{
          items: jobBreadcrumbs(selectedJob, undefined, 'Transmittals'),
        }}
        helpText="View and manage transmittals sent for this job."
        actions={
          <WorkflowCreateButton
            workflow={selectedJob.transmittalWorkflow}
            color={resolveWorkflowButtonColor(
              selectedJob.transmittalWorkflow?.createButtonColor,
            )}
            leftSection={<IconPlus />}
            onClick={() => navigate(`${location.pathname}/submit/transmittal`)}
          >
            {selectedJob.transmittalWorkflow?.createButtonText ||
              'Create Transmittal'}
          </WorkflowCreateButton>
        }
      />

      <Group mb="md">
        <SecondaryButton leftSection={<IconFileText />} onClick={handleOpenLog}>
          Transmittal Log
        </SecondaryButton>
      </Group>

      {!selectedJob.transmittalWorkflowId && (
        <Alert mb="md" title="Missing Transmittal Workflow" color="red">
          Default workflow does not exist. Please contact an administrator.
        </Alert>
      )}

      <WorkflowStatusPanel
        segments={segments}
        value={statusFilter}
        onChange={setStatusFilter}
      />

      <TransmittalsTable
        transmittals={filteredItems}
        loading={loading}
        onRefresh={() => {
          refetch();
        }}
      />

      <WorkflowLogModal
        opened={logOpen}
        onClose={closeLog}
        title="Transmittal Log"
        pdfTitle="Transmittal Log PDF"
        statusOptions={selectableStatuses}
        statuses={logStatuses}
        onStatusChange={setLogStatuses}
        customerNumber={selectedJob.customerNumber?.trim() ?? ''}
        contact={logContact}
        onContactChange={setLogContact}
        logUrl={logUrl}
        logLoading={logLoading}
      />
    </div>
  );
};
